import moment from 'moment'

import type { GetChatResponse, TripPlannerChat } from 'tripPlanner/api/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { getDestinationsLabel } from 'tripPlanner/utils/places'
import { stripContextTags } from 'tripPlanner/utils/contextTag'

export type UserMessageSubtitleByChatId = Map<number, string>

export type RecentChatItem =
  | {
    type: 'aiChat'
    id: number
    title?: string
    subtitle?: string
    updatedAt: string
    sortTimestamp: number
  }
  | {
    type: 'classicTrip'
    id: string
    title: string
    subtitle?: string
    updatedAt: string
    sortTimestamp: number
  }

function getTimeValue(date: string | moment.Moment | undefined): number {
  if (!date) return 0
  if (moment.isMoment(date)) {
    const time = date.valueOf()
    return Number.isNaN(time) ? 0 : time
  }

  const parsedDate = new Date(date)
  const time = parsedDate.getTime()
  return Number.isNaN(time) ? 0 : time
}

function getClassicTripSubtitle(trip: BasicTrip): string | undefined {
  if (!trip.destinationsGeo || trip.destinationsGeo.length === 0) {
    return undefined
  }

  return getDestinationsLabel(trip.destinationsGeo, 1)
}

export function getLatestUserMessageSubtitle(chat: GetChatResponse | undefined): string | undefined {
  const latestUserMessage = chat?.messages.findLast(message => message.role.toLowerCase() !== 'assistant')
  const visibleContent = latestUserMessage ? stripContextTags(latestUserMessage.content) : ''
  return visibleContent || undefined
}

export function buildUserMessageSubtitleByChatId(
  chatIds: Array<number>,
  chats: Array<GetChatResponse | undefined>,
): UserMessageSubtitleByChatId {
  const map: UserMessageSubtitleByChatId = new Map()
  chatIds.forEach((chatId, index) => {
    const subtitle = getLatestUserMessageSubtitle(chats[index])
    if (subtitle) {
      map.set(chatId, subtitle)
    }
  })
  return map
}

export function buildRecentChatItems(
  chats: Array<TripPlannerChat>,
  trips: Array<BasicTrip>,
): Array<RecentChatItem> {
  const aiChatItems: Array<RecentChatItem> = chats.map((chat) => {
    const updatedAt = getChatLastActivityTimestamp(chat)
    return {
      type: 'aiChat',
      id: chat.id,
      title: chat.name,
      updatedAt,
      sortTimestamp: getTimeValue(updatedAt),
    }
  })

  const classicTripItems: Array<RecentChatItem> = trips.map((trip) => {
    const updatedAt = trip.updatedAt.isValid() ? trip.updatedAt.toISOString() : ''
    return {
      type: 'classicTrip',
      id: trip.id,
      title: trip.name,
      subtitle: getClassicTripSubtitle(trip),
      updatedAt,
      sortTimestamp: getTimeValue(trip.updatedAt),
    }
  })

  return [...aiChatItems, ...classicTripItems].sort((a, b) => b.sortTimestamp - a.sortTimestamp)
}
